import {
  AlertTriangle,
  ArrowRightLeft,
  BarChart3,
  Bell,
  Building,
  CheckCircle,
  Construction,
  FileSpreadsheet,
  FileText,
  FolderOpen,
  Globe,
  Landmark,
  LayoutDashboard,
  type LucideIcon,
  Package,
  Palette,
  PenTool,
  PieChart,
  Send,
  Shield,
  Target,
  TrendingUp,
  UserPlus,
  Users,
  Wallet,
} from 'lucide-react';
import { Link, useLocation } from 'react-router';
import { type AppRole, FeatureFlag } from '@metycle/shared';

import { ComingSoonTag } from '@/components/coming-soon-tag';
import { NavBadge } from '@/components/layout/nav-badge';
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem } from '@/components/ui/sidebar-menu';
import {
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
} from '@/components/ui/sidebar-sections';
import { useCurrentUser } from '@/features/auth';
import { useFeatureFlag } from '@/lib/feature-flags';
import { NAV_GROUPS } from '@/config/navigation';
import type { NavGroup, NavIconKey, NavItem } from '@/config/navigation-types';

const ICONS = {
  building: Building,
  'layout-dashboard': LayoutDashboard,
  'trending-up': TrendingUp,
  'bar-chart-3': BarChart3,
  'file-text': FileText,
  package: Package,
  globe: Globe,
  'check-circle': CheckCircle,
  construction: Construction,
  'pen-tool': PenTool,
  'folder-open': FolderOpen,
  target: Target,
  'alert-triangle': AlertTriangle,
  'pie-chart': PieChart,
  wallet: Wallet,
  'arrow-right-left': ArrowRightLeft,
  landmark: Landmark,
  'file-spreadsheet': FileSpreadsheet,
  send: Send,
  shield: Shield,
  'user-plus': UserPlus,
  users: Users,
  palette: Palette,
  bell: Bell,
} satisfies Record<NavIconKey, LucideIcon>;

const INVESTOR_KIOSK_ITEM: NavItem = {
  id: 'investor-gmv',
  slug: 'finance/gmv',
  label: 'GMV Overview',
  iconKey: 'trending-up',
};

const INVESTOR_KIOSK_GROUP: NavGroup = {
  id: 'investor-kiosk',
  label: 'Finance Views',
  items: [INVESTOR_KIOSK_ITEM],
};

function hasAnyRole(roles: readonly AppRole[], required: readonly AppRole[] | undefined): boolean {
  if (!required || required.length === 0) return true;
  return required.some((r) => roles.includes(r));
}

export function filterNavForRoles(groups: readonly NavGroup[], roles: AppRole[]): NavGroup[] {
  return groups
    .filter((group) => hasAnyRole(roles, group.requiredRoles))
    .map((group) => ({
      ...group,
      items: group.items.filter((item) => hasAnyRole(roles, item.requiredRoles)),
    }))
    .filter((group) => group.items.length > 0);
}

function NavMenuItem({ item, isActive }: Readonly<{ item: NavItem; isActive: boolean }>) {
  const Icon = ICONS[item.iconKey];
  return (
    <SidebarMenuItem>
      <SidebarMenuButton asChild isActive={isActive} tooltip={item.label}>
        <Link to={`/${item.slug}`}>
          <Icon />
          <span className="min-w-0 flex-1 truncate">{item.label}</span>
          {item.comingSoon && <ComingSoonTag className="ml-auto shrink-0" />}
          {item.badgeKey !== undefined && <NavBadge badgeKey={item.badgeKey} />}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function SidebarNav() {
  const { pathname } = useLocation();
  const { roles, isLoading, isInvestorOnly } = useCurrentUser();
  const invoicingV2Enabled = useFeatureFlag(FeatureFlag.INVOICING_V2, false);

  const flagAdmittedGroups = NAV_GROUPS.filter(
    (group) => group.featureFlag !== FeatureFlag.INVOICING_V2 || invoicingV2Enabled,
  );

  // Default-deny while loading so a forbidden link never flashes before the profile resolves.
  const effectiveRoles = isLoading ? [] : roles;
  const visibleGroups = isInvestorOnly
    ? [INVESTOR_KIOSK_GROUP]
    : filterNavForRoles(flagAdmittedGroups, effectiveRoles);

  return (
    <SidebarContent>
      {visibleGroups.map((group) => (
        <SidebarGroup key={group.id}>
          <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {group.items.map((item) => (
                <NavMenuItem key={item.id} item={item} isActive={pathname === `/${item.slug}`} />
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      ))}
    </SidebarContent>
  );
}
